import { useState, useRef, useCallback, useEffect } from "react";
import {
  getSpeechRecognitionConstructor,
  isSpeechRecognitionSupported,
  isMediaRecorderSupported,
  requestMicrophonePermission,
  checkMicrophonePermission,
  describeMicrophoneAccessError,
  type MicrophoneAccessIssue,
  type SpeechRecognitionInstance,
  type SpeechRecognitionEvent,
  type SpeechRecognitionErrorEvent,
} from "@/lib/speechUtils";

export type SpeechMode = "auto" | "webspeech" | "whisper";

interface UseSpeechToTextOptions {
  language?: string;
  continuous?: boolean;
  mode?: SpeechMode;
  onTranscript?: (text: string) => void;
  onInterim?: (text: string) => void;
  onError?: (error: string, issue?: MicrophoneAccessIssue) => void;
  maxDuration?: number; // seconds
}

interface UseSpeechToTextReturn {
  isListening: boolean;
  isProcessing: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  duration: number;
  audioLevel: number;
  startListening: () => Promise<void>;
  stopListening: () => void;
  cancelListening: () => void;
  isSupported: boolean;
}

function resolveMode(preferred: SpeechMode): "webspeech" | "whisper" | null {
  if (preferred === "webspeech") {
    return isSpeechRecognitionSupported() ? "webspeech" : null;
  }
  if (preferred === "whisper") {
    return isMediaRecorderSupported() ? "whisper" : null;
  }
  if (isSpeechRecognitionSupported()) return "webspeech";
  if (isMediaRecorderSupported()) return "whisper";
  return null;
}

export function useSpeechToText(options: UseSpeechToTextOptions = {}): UseSpeechToTextReturn {
  const {
    language = "en-US",
    continuous = true,
    mode = "auto",
    onTranscript,
    onInterim,
    onError,
    maxDuration = 120,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const accumulatedTextRef = useRef("");

  const resolvedMode = resolveMode(mode);
  const isSupported = resolvedMode !== null;

  const stopAudioAnalysis = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = 0;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  const startAudioAnalysis = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(avg / 128, 1));
        animFrameRef.current = requestAnimationFrame(tick);
      };

      tick();
    } catch {
      // Audio analysis is non-critical.
    }
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    setDuration(0);
    timerRef.current = setInterval(() => {
      setDuration((value) => value + 1);
    }, 1000);
  }, []);

  const releaseStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    stopTimer();
    stopAudioAnalysis();
    releaseStream();
    setIsListening(false);
    setIsProcessing(false);
    setInterimTranscript("");
  }, [stopTimer, stopAudioAnalysis, releaseStream]);

  const startWebSpeech = useCallback(async () => {
    const SRConstructor = getSpeechRecognitionConstructor();
    if (!SRConstructor) {
      setError("Speech recognition is not supported in this browser.");
      onError?.("Speech recognition is not supported in this browser.");
      return;
    }

    const recognition = new SRConstructor();
    recognition.lang = language;
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    accumulatedTextRef.current = "";

    recognition.onresult = (event) => {
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (final) {
        accumulatedTextRef.current += (accumulatedTextRef.current ? " " : "") + final;
        setTranscript(accumulatedTextRef.current);
        onTranscript?.(accumulatedTextRef.current);
      }

      if (interim) {
        const fullInterim = accumulatedTextRef.current
          ? accumulatedTextRef.current + " " + interim
          : interim;
        setInterimTranscript(fullInterim);
        onInterim?.(fullInterim);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "no-speech") {
        setError("No speech detected. Please try again.");
        onError?.("No speech detected.");
        cleanup();
        return;
      }

      if (event.error === "aborted") {
        cleanup();
        return;
      }

      cleanup();
      void describeMicrophoneAccessError(event.error).then((issue) => {
        setError(issue.message);
        onError?.(issue.message, issue);
      });
    };

    recognition.onend = () => {
      if (!cancelledRef.current && accumulatedTextRef.current) {
        setTranscript(accumulatedTextRef.current);
        onTranscript?.(accumulatedTextRef.current);
      }
      cleanup();
    };

    recognitionRef.current = recognition;
    cancelledRef.current = false;
    setError(null);
    setTranscript("");
    setInterimTranscript("");
    setIsListening(true);
    startTimer();

    try {
      recognition.start();
    } catch (error) {
      cleanup();
      const issue = await describeMicrophoneAccessError(error);
      setError(issue.message);
      onError?.(issue.message, issue);
      return;
    }

    checkMicrophonePermission().then((state) => {
      if (state === "granted") {
        requestMicrophonePermission()
          .then((stream) => {
            mediaStreamRef.current = stream;
            startAudioAnalysis(stream);
          })
          .catch(() => {});
      }
    });
  }, [
    language,
    continuous,
    onTranscript,
    onInterim,
    onError,
    startAudioAnalysis,
    startTimer,
    cleanup,
  ]);

  const startWhisper = useCallback(async () => {
    let stream: MediaStream;

    try {
      stream = await requestMicrophonePermission();
      mediaStreamRef.current = stream;
    } catch (error) {
      const issue = await describeMicrophoneAccessError(error);
      setError(issue.message);
      onError?.(issue.message, issue);
      return;
    }

    startAudioAnalysis(stream);
    chunksRef.current = [];

    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";

    const recorder = new MediaRecorder(stream, { mimeType });

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = async () => {
      if (cancelledRef.current) {
        cleanup();
        return;
      }

      setIsListening(false);
      setIsProcessing(true);
      stopTimer();
      stopAudioAnalysis();

      const blob = new Blob(chunksRef.current, { type: mimeType });

      try {
        const formData = new FormData();
        formData.append("file", blob, "recording.webm");
        if (language) {
          formData.append("language", language.split("-")[0]);
        }

        const response = await fetch("/api/transcribe", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || "Transcription failed");
        }

        const data = await response.json();
        if (data.text) {
          setTranscript(data.text);
          onTranscript?.(data.text);
        }
      } catch (error: any) {
        const message = error.message || "Could not transcribe audio. Please check your connection.";
        setError(message);
        onError?.(message);
      } finally {
        setIsProcessing(false);
        releaseStream();
      }
    };

    mediaRecorderRef.current = recorder;
    cancelledRef.current = false;
    setError(null);
    setTranscript("");
    setInterimTranscript("");
    setIsListening(true);
    startTimer();
    recorder.start(1000);
  }, [
    language,
    onTranscript,
    onError,
    startAudioAnalysis,
    stopAudioAnalysis,
    startTimer,
    stopTimer,
    cleanup,
    releaseStream,
  ]);

  const startListening = useCallback(async () => {
    if (isListening || isProcessing) return;

    if (resolvedMode === "webspeech") {
      await startWebSpeech();
    } else if (resolvedMode === "whisper") {
      await startWhisper();
    } else {
      setError("Voice input is not supported in your browser. Try Chrome or Edge.");
      onError?.("Voice input not supported.");
    }
  }, [isListening, isProcessing, resolvedMode, startWebSpeech, startWhisper, onError]);

  const stopListening = useCallback(() => {
    cancelledRef.current = false;

    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
  }, []);

  const cancelListening = useCallback(() => {
    cancelledRef.current = true;

    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    setTranscript("");
    setInterimTranscript("");
    cleanup();
  }, [cleanup]);

  useEffect(() => {
    if (isListening && duration >= maxDuration) {
      stopListening();
    }
  }, [isListening, duration, maxDuration, stopListening]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      cleanup();
    };
  }, [cleanup]);

  return {
    isListening,
    isProcessing,
    transcript,
    interimTranscript,
    error,
    duration,
    audioLevel,
    startListening,
    stopListening,
    cancelListening,
    isSupported,
  };
}
